import { createAsyncThunk } from '@reduxjs/toolkit';
import authApi from 'src/features/auth/api';
import { getPaginationRequest, IInvitation, IInvitationRequest } from 'ts/interfaces';
import apiCompany from '../company/api';
import api from './api';
import { NAME } from './constants';

export const invitationGetByIdThunk = createAsyncThunk(`${NAME}/getById`, async (id: string) => {
  try {
    const { data } = await api.getById(id);

    return data as IInvitation;
  } catch (error) {
    console.error(`${NAME}/getById`, error);
    throw new Error('Invitation not found');
  }
});

// PIN-flow thunks rejectWithValue(numericStatus) — unlike the throw-new-Error thunks
// elsewhere in this file — so reducers can map per-state error copy by HTTP status.
// They deliberately do NOT console.error the axios error: its `config.data` carries the
// submitted OTP in plaintext, which must never reach devtools / the browser logging SDK.
export const invitationSendOtpThunk = createAsyncThunk(
  `${NAME}/sendOtp`,
  async (id: string, { rejectWithValue }) => {
    try {
      // obfuscated_email already lives in getOne.data from the GET — nothing to return.
      await api.requestOtp(id);
      return true;
    } catch (error: any) {
      return rejectWithValue(error?.response?.status ?? null);
    }
  }
);

export const invitationVerifyOtpThunk = createAsyncThunk(
  `${NAME}/verifyOtp`,
  async ({ id, otp }: { id: string; otp: string }, { rejectWithValue }) => {
    try {
      const body = await api.verifyOtp(id, otp);
      // Returned in the action payload → InvitationPage reads it into component state.
      // NOT written into getOne.data, and the token is NOT stored in Redux.
      return { invitation: body.data, token: body.token };
    } catch (error: any) {
      return rejectWithValue(error?.response?.status ?? null);
    }
  }
);

export const invitationCreateUserWithTokenThunk = createAsyncThunk(
  `${NAME}/createUserWithToken`,
  async (
    { id, password, token }: { id: string; password: string; token: string },
    { rejectWithValue }
  ) => {
    try {
      await api.createUserWithToken(id, password, token);
      return authApi.login();
    } catch (error: any) {
      return rejectWithValue(error?.response?.status ?? null);
    }
  }
);

export const getAll = createAsyncThunk(`${NAME}/getAll`, async (data: getPaginationRequest) => {
  try {
    return await api.getAll(data);
  } catch (error) {
    console.error(`${NAME}/getAll`, error);
    throw new Error('Error fetching the invitations');
  }
});

export const getByEmail = createAsyncThunk(
  `${NAME}/getByEmail`,
  async (data: getPaginationRequest) => {
    try {
      return await api.getAll(data);
    } catch (error) {
      console.error(`${NAME}/getByEmail`, error);
      throw new Error('Error fetching the invitation by email');
    }
  }
);

export const remove = createAsyncThunk(`${NAME}/delete`, async (invitationId: string) => {
  try {
    return await api.delete(invitationId);
  } catch (error) {
    console.error(`${NAME}/delete`, error);
    throw new Error('Error deleting the invitation');
  }
});

export const resendInvitation = createAsyncThunk(
  `${NAME}/resendInvitation`,
  async (invitationId: string) => {
    try {
      await api.resendInvitation(invitationId);
      return { invitationId };
    } catch (error) {
      console.error(`${NAME}/resendInvitation`, error);
      throw new Error('Error resending the invitation');
    }
  }
);

export const create = createAsyncThunk(`${NAME}/create`, async (data: IInvitationRequest) => {
  try {
    return await api.create(data);
  } catch (error) {
    console.error(`${NAME}/create`, error);
    throw new Error('Error creating the invitation');
  }
});

export const searchGetAllCompany = createAsyncThunk(
  `${NAME}/searchGetAllCompany`,
  async (data: getPaginationRequest) => {
    try {
      return await apiCompany.getAll(data);
    } catch (error) {
      console.error(`${NAME}/searchGetAllCompany`, error);
      throw new Error('Error fetching the companies from searchGetAllCompany');
    }
  }
);
